import { useMemo } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { OrganizationEventType } from '@/gen/cortado/v1/settings_pb'
import { useSystemSettings } from '@/stores/settings.store'
import type { BadgeVariant } from '@/lib/enum-utils'
import { resolveEventTypeColor, resolveEventTypeIcon } from '@/lib/event-types'

export interface UseEventTypesResult {
  /**
   * Every event type configured for the active organization, archived ones
   * included, sorted by `displayOrder`.
   *
   * Use this for filters and display — archived types must stay visible so
   * historical records remain readable and filterable.
   */
  eventTypes: OrganizationEventType[]
  /**
   * Only the non-archived types, sorted by `displayOrder`.
   *
   * Use this for create/edit dropdowns — an archived type must not be
   * assignable to new records.
   */
  activeEventTypes: OrganizationEventType[]
  /**
   * Options for a create/edit dropdown: `activeEventTypes`, plus the record's
   * current type when that type has since been archived.
   *
   * Archived types stay unassignable to *other* records; including the one
   * already on this record just keeps the field from rendering blank (and from
   * being silently cleared by an unrelated edit).
   */
  getEventTypeOptions: (currentKey: string | undefined) => OrganizationEventType[]
  /** Look up a type by its immutable `key`. */
  getEventType: (key: string | undefined) => OrganizationEventType | undefined
  /**
   * Display label for a key. Falls back to the raw key when the type is
   * unknown (e.g. a record written before the type was removed), and to an
   * empty string when there is no key at all.
   */
  getEventTypeLabel: (key: string | undefined) => string
  getEventTypeColor: (key: string | undefined) => BadgeVariant
  getEventTypeIcon: (key: string | undefined) => LucideIcon
  isLoading: boolean
}

function byDisplayOrder(a: OrganizationEventType, b: OrganizationEventType) {
  return a.displayOrder - b.displayOrder
}

/**
 * Single source of truth for the active organization's event types.
 *
 * Event types are per-organization data (not a proto enum) delivered on
 * `Settings.organizationEventTypes`; records reference them by the immutable
 * `key` string.
 */
export function useEventTypes(): UseEventTypesResult {
  const { settings, isLoading } = useSystemSettings()

  return useMemo(() => {
    const eventTypes = [...(settings?.organizationEventTypes ?? [])].sort(byDisplayOrder)
    const activeEventTypes = eventTypes.filter((type) => type.isActive)
    const byKey = new Map(eventTypes.map((type) => [type.key, type]))

    const getEventType = (key: string | undefined) =>
      key ? byKey.get(key) : undefined

    return {
      eventTypes,
      activeEventTypes,
      getEventTypeOptions: (currentKey) => {
        const current = getEventType(currentKey)
        if (!current || current.isActive) return activeEventTypes
        return [...activeEventTypes, current].sort(byDisplayOrder)
      },
      getEventType,
      getEventTypeLabel: (key) => (key ? (getEventType(key)?.label ?? key) : ''),
      getEventTypeColor: (key) => resolveEventTypeColor(getEventType(key)?.color),
      getEventTypeIcon: (key) => resolveEventTypeIcon(getEventType(key)?.icon),
      isLoading,
    } satisfies UseEventTypesResult
  }, [settings?.organizationEventTypes, isLoading])
}
